import { GENERATED_SUPPORTED_LANGUAGES } from "./generatedSupportedLanguages";

type I18nState = {
  ready: boolean;
  language: string;
  defaultLanguage: string;
  translations: Record<string, string>;
  runtimeTranslations: Record<string, string>;
  runtimeTranslationBundles: Record<string, Record<string, string>>;
  supportedLanguages: string[];
};

export type InitI18nOptions = {
  supportedLanguages?: string[];
  userLanguage?: string | null;
  browserLanguage?: string | null;
  defaultLanguage?: string | null;
  preserveStoredLanguage?: boolean;
};

const TRANSLATIONS_CATALOG = "volqan-consumer-app";
const TRANSLATIONS_BASE_URL = "/trans";
const LANGUAGE_STORAGE_KEY = "volqan.consumer.language";
const listeners = new Set<() => void>();

const state: I18nState = {
  ready: false,
  language: "en",
  defaultLanguage: "en",
  translations: {},
  runtimeTranslations: {},
  runtimeTranslationBundles: {},
  supportedLanguages: Array.from(
    new Set(
      (GENERATED_SUPPORTED_LANGUAGES as readonly string[])
        .map((code) => String(code || "").trim().slice(0, 2).toLowerCase())
        .filter(Boolean)
    )
  ),
};

let cachedSnapshot: I18nState = { ...state };
let inMemoryLanguage: string | null = null;

const canUseBrowserStorage = (): boolean =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const getTranslationCacheKey = (catalog: string, language: string): string =>
  `volqan.i18n.cache.${catalog}.${language}`;

const readCachedTranslations = (catalog: string, language: string): Record<string, string> => {
  if (!canUseBrowserStorage()) return {};
  try {
    const raw = window.localStorage.getItem(getTranslationCacheKey(catalog, language));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const entries = Object.entries(parsed).filter(([, value]) => typeof value === "string");
    return Object.fromEntries(entries) as Record<string, string>;
  } catch {
    return {};
  }
};

const writeCachedTranslations = (catalog: string, language: string, dictionary: Record<string, string>): void => {
  if (!canUseBrowserStorage()) return;
  try {
    window.localStorage.setItem(getTranslationCacheKey(catalog, language), JSON.stringify(dictionary));
  } catch {
    // ignore quota errors
  }
};

const refreshSnapshot = () => {
  cachedSnapshot = {
    ready: state.ready,
    language: state.language,
    defaultLanguage: state.defaultLanguage,
    translations: state.translations,
    runtimeTranslations: state.runtimeTranslations,
    runtimeTranslationBundles: state.runtimeTranslationBundles,
    supportedLanguages: state.supportedLanguages,
  };
};

const notify = () => {
  refreshSnapshot();
  listeners.forEach((listener) => listener());
};

const normalizeLanguage = (value?: string | null): string =>
  String(value || "").trim().slice(0, 2).toLowerCase();

const pickSupportedLanguage = (
  candidate: string | null | undefined,
  supported: string[]
): string | null => {
  const normalized = normalizeLanguage(candidate);
  return supported.includes(normalized) ? normalized : null;
};

const getStoredLanguage = async () => {
  if (inMemoryLanguage) return inMemoryLanguage;
  if (!canUseBrowserStorage()) return null;
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (!stored) return null;
    inMemoryLanguage = stored;
    return stored;
  } catch {
    return null;
  }
};

const setStoredLanguage = async (language: string) => {
  inMemoryLanguage = String(language || "").trim() || null;
  if (!canUseBrowserStorage()) return;
  try {
    if (inMemoryLanguage) window.localStorage.setItem(LANGUAGE_STORAGE_KEY, inMemoryLanguage);
    else window.localStorage.removeItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
};

const loadTranslations = async (language: string): Promise<Record<string, string>> => {
  try {
    const response = await fetch(`${TRANSLATIONS_BASE_URL}/${language}.json`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error(`HTTP_${response.status}`);
    const payload = await response.json();
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) return {};
    const entries = Object.entries(payload as Record<string, unknown>).filter(([, value]) => typeof value === "string");
    const dictionary = Object.fromEntries(entries) as Record<string, string>;
    writeCachedTranslations(TRANSLATIONS_CATALOG, language, dictionary);
    return dictionary;
  } catch {
    return readCachedTranslations(TRANSLATIONS_CATALOG, language);
  }
};

const normalizeRuntimeTranslationBundles = (
  bundles: Record<string, unknown> | null | undefined
): Record<string, Record<string, string>> =>
  Object.fromEntries(
    Object.entries(bundles || {})
      .map(([language, dictionary]) => [
        normalizeLanguage(language),
        Object.fromEntries(
          Object.entries(
            dictionary && typeof dictionary === "object" && !Array.isArray(dictionary)
              ? (dictionary as Record<string, unknown>)
              : {}
          ).filter(([, value]) => typeof value === "string")
        ) as Record<string, string>,
      ])
      .filter(([language, dictionary]) => language && Object.keys(dictionary).length > 0)
  );

const refreshRuntimeTranslationsForCurrentLanguage = () => {
  const defaultBundle = state.runtimeTranslationBundles.default || {};
  const currentBundle =
    state.language === "default"
      ? defaultBundle
      : state.runtimeTranslationBundles[state.language] || {};
  state.runtimeTranslations =
    state.language === "default"
      ? { ...defaultBundle }
      : { ...defaultBundle, ...currentBundle };
};

const escapeRegex = (input: string) => input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const replacePlaceholderToken = (input: string, placeholder: string, value: string): string => {
  const safe = escapeRegex(placeholder);
  const angularPattern = new RegExp(`\\{\\{\\s*${safe}\\s*\\}\\}`, "g");
  const percentPattern = new RegExp(`%${safe}%`, "g");
  const bracePattern = new RegExp(`\\{${safe}\\}`, "g");
  return input.replace(angularPattern, value).replace(percentPattern, value).replace(bracePattern, value);
};

const replaceSingleUnknownPlaceholder = (
  input: string,
  replacements?: Record<string, string | number> | null
): string => {
  if (!replacements || typeof replacements !== "object") return input;
  const entries = Object.entries(replacements);
  if (entries.length !== 1) return input;

  const [[knownPlaceholder, knownValue]] = entries;
  const unknownPlaceholders = Array.from(
    new Set(
      [...input.matchAll(/\{([^{}]+)\}/g)]
        .map((match) => String(match[1] || "").trim())
        .filter((token) => token && token !== knownPlaceholder)
    )
  );

  if (unknownPlaceholders.length !== 1) return input;
  return replacePlaceholderToken(input, unknownPlaceholders[0], String(knownValue));
};

const applyReplacements = (
  text: string,
  replacements?: Record<string, string | number> | null
): string => {
  if (!replacements || typeof replacements !== "object") return text;
  const replaced = Object.entries(replacements).reduce(
    (result, [placeholder, value]) => replacePlaceholderToken(result, placeholder, String(value)),
    text
  );
  return replaceSingleUnknownPlaceholder(replaced, replacements);
};

export const initI18n = async (options: InitI18nOptions = {}): Promise<void> => {
  const supported = Array.from(
    new Set(
      (options.supportedLanguages?.length ? options.supportedLanguages : state.supportedLanguages)
        .map((entry) => normalizeLanguage(entry))
        .filter(Boolean)
    )
  );

  if (supported.length) {
    state.supportedLanguages = supported;
  }

  const stored = options.preserveStoredLanguage === false ? null : await getStoredLanguage();
  const resolvedDefaultLanguage =
    pickSupportedLanguage(options.defaultLanguage, state.supportedLanguages) ||
    state.supportedLanguages[0] ||
    "en";
  const chosen =
    pickSupportedLanguage(options.userLanguage, state.supportedLanguages) ||
    pickSupportedLanguage(stored, state.supportedLanguages) ||
    pickSupportedLanguage(options.browserLanguage || navigator.language, state.supportedLanguages) ||
    resolvedDefaultLanguage;

  state.defaultLanguage = resolvedDefaultLanguage;
  state.language = chosen;
  state.translations = await loadTranslations(chosen);
  refreshRuntimeTranslationsForCurrentLanguage();
  state.ready = true;
  notify();
};

export const setLanguage = async (nextLanguage: string): Promise<void> => {
  const normalized = pickSupportedLanguage(nextLanguage, state.supportedLanguages);
  if (!normalized) return;
  state.language = normalized;
  state.translations = await loadTranslations(normalized);
  refreshRuntimeTranslationsForCurrentLanguage();
  await setStoredLanguage(normalized);
  state.ready = true;
  notify();
};

export const resetLanguage = async (): Promise<void> => {
  const normalized =
    pickSupportedLanguage(state.defaultLanguage, state.supportedLanguages) ||
    state.supportedLanguages[0] ||
    "en";
  state.language = normalized;
  state.translations = await loadTranslations(normalized);
  refreshRuntimeTranslationsForCurrentLanguage();
  await setStoredLanguage(normalized);
  state.ready = true;
  notify();
};

export const getI18nState = () => cachedSnapshot;

export const setRuntimeTranslations = (translations: Record<string, string>): void => {
  const entries = Object.entries(translations || {}).filter(([, value]) => typeof value === "string");
  state.runtimeTranslationBundles = {
    ...state.runtimeTranslationBundles,
    [state.language]: Object.fromEntries(entries),
  };
  refreshRuntimeTranslationsForCurrentLanguage();
  notify();
};

export const setRuntimeTranslationsBundles = (bundles: Record<string, unknown>): void => {
  state.runtimeTranslationBundles = normalizeRuntimeTranslationBundles(bundles);
  refreshRuntimeTranslationsForCurrentLanguage();
  notify();
};

export const trnInternal = (
  key: string,
  defaultTranslation: string,
  replacements: Record<string, string | number> | null = null,
  _instructions?: string
): string => {
  const base = state.runtimeTranslations[key] || state.translations[key] || defaultTranslation || key;
  return applyReplacements(base, replacements);
};

export const i18nStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getSnapshot() {
    return cachedSnapshot;
  },
};
